import React, { useRef, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Modal from 'components/shared/Modal/Modal';
import { IconAndText } from 'components/shared/SvgAF/SvgAF';
import WarningBox from 'components/shared/WarningBox/WarningBox';
import { IconLoader } from 'common/IconLoader';
import { v4 } from 'uuid';
import './TwoHopPivotDetails.css';
import { ReactTable } from '../../../shared/ReactTable';
import { getRhumbLineBearing, getDistance } from 'geolib';
import { patchDeviceParameters } from 'redux/actions';
import { TWO_HOP_ALIGNMENT_SCREEN_INDEXES } from '../utils';

const DISTANCE_WARNING_MESSAGE =
  'WARNING: The calculated averrage of Distance between Pivot1 and Pivot2 exceeds 80 meters. The results produced by the Two-Hop algorithms may not be valid.';

const AZ_WARNING_MESSAGE =
  'WARNING: The calculated standard deviation of Azimuth between Pivot1 and Pivot2 exceeds 15 degrees. The results produced by the Two-Hop algorithms may not be valid.';

export const TwoHopPivotDetails = (props) => {
  const [renderKey, setRenderKey] = useState(false);
  const hop1Data = useRef([]);
  const hop2Data = useRef([]);
  const hopDistanceData = useRef([]);
  const hopAzData = useRef([]);
  const [pivot1AzValues, setPivot1AzValues] = useState([]);
  const [pivot2AzValues, setPivot2AzValues] = useState([]);
  const [AzStdDevs, setAzStdDevs] = useState([]);
  const [editingRowString, setEditingRowString] = useState();
  const azChangeDictionary = useRef({});
  let dispatch = useDispatch();

  const mounted = useRef();
  useEffect(() => {
    if (!mounted.current) {
      // do componentDidMount logic
      createData();
      mounted.current = true;
    }
  });

  useEffect(() => {
    const stdDev1 = getStdDev(0);
    const stdDev2 = getStdDev(1);

    setAzStdDevs([stdDev1, stdDev2]);
  }, [pivot1AzValues, pivot2AzValues]);

  const hopColumns = (parent) => [
    {
      Header: (
        <div className="HopDetailsTableColumnHeader" data-tip="Pivot">
          Pivot
        </div>
      ),
      minWidth: 220,
      accessor: 'pivot',
      sortable: false,
      Cell: (row) => {
        let label = null;

        if (row.original.pivot === 'pivot1' && row.index === 0)
          label = (
            <div>
              <IconAndText img={IconLoader.icon_donor} text={'DU1'} />
              <img src={IconLoader.right_arrow} alt="" />
              <div>gNB</div>
            </div>
          );
        if (row.original.pivot === 'pivot1' && row.index === 1)
          label = (
            <div>
              <IconAndText img={IconLoader.icon_service} text={'SU1 HBF'} />
              <img src={IconLoader.right_arrow} alt="" />
              <IconAndText img={IconLoader.icon_donor} text={'Hop2 DU'} />
            </div>
          );
        if (row.original.pivot === 'pivot1' && row.index === 2)
          label = (
            <div>
              <IconAndText img={IconLoader.icon_service} text={'SU1 WB1'} />
              <img src={IconLoader.right_arrow} alt="" />
              <div>Bldg</div>
            </div>
          );

        if (row.original.pivot === 'pivot2' && row.index === 0)
          label = (
            <div>
              <IconAndText img={IconLoader.icon_donor} text={'DU2'} />
              <img src={IconLoader.right_arrow} alt="" />
              <IconAndText img={IconLoader.icon_service} text={'SU1 HBF'} />
            </div>
          );
        if (row.original.pivot === 'pivot2' && row.index === 1)
          label = (
            <div>
              <IconAndText img={IconLoader.icon_service} text={'SU2 WB1'} />
              <img src={IconLoader.right_arrow} alt="" />
              <div>Bldg</div>
            </div>
          );
        if (row.original.pivot === 'pivot2' && row.index === 2)
          label = (
            <div>
              <IconAndText img={IconLoader.icon_service} text={'SU2 WB1'} />
              <img src={IconLoader.right_arrow} alt="" />
              <div>Bldg</div>
            </div>
          );

        return <div id="hopPivotLabel">{label}</div>;
      }
    },
    {
      Header: (
        <div className="HopDetailsTableColumnHeader" data-tip={'DEVICE ID/USER LABEL'}>
          DEVICE ID/USER LABEL
        </div>
      ),
      minWidth: 250,
      width: 250,
      accessor: null,
      sortable: false,
      Cell: (row) => {
        let idLabel = null;
        let nameLabel = null;
        idLabel = row.original.deviceIdUserLabel.deviceId;
        nameLabel = row.original.deviceIdUserLabel.userLabel;
        return (
          <div>
            <div>{idLabel}</div>
            <div>{nameLabel}</div>
          </div>
        );
      }
    },
    {
      Header: (
        <div className="HopDetailsTableColumnHeader" data-tip={'PRODUCT TYPE'}>
          PRODUCT TYPE
        </div>
      ),
      minWidth: 170,
      width: 170,
      accessor: 'productType',
      sortable: false,
      Cell: (row) => <div className="HopDistanceCellWrapper">{row.original.productType}</div>
    },
    {
      Header: (
        <div className="HopDetailsTableColumnHeader" data-tip={'STATUS'}>
          STATUS
        </div>
      ),
      accessor: 'pivotStatus',
      sortable: false,
      Cell: (row) => <div className="HopDistanceCellWrapper">{row.original.pivotStatus}</div>
    },
    {
      Header: (
        <div className="HopDetailsTableColumnHeader" data-tip={'TWIN LAT'}>
          TWIN LAT
        </div>
      ),
      accessor: 'lat',
      sortable: false,
      Cell: (row) => (
        <div className="HopDistanceCellWrapper" data-tip={row.original.lat}>
          {getTruncatedCellDisplay(row.original.lat)}
        </div>
      )
    },
    {
      Header: (
        <div className="HopDetailsTableColumnHeader" data-tip={'TWIN LON'}>
          TWIN LON
        </div>
      ),
      accessor: 'long',
      sortable: false,
      Cell: (row) => (
        <div className="HopDistanceCellWrapper" data-tip={row.original.long}>
          {getTruncatedCellDisplay(row.original.long)}
        </div>
      )
    },
    {
      Header: (
        <div className="HopDetailsTableColumnHeader" data-tip={'BEAM AZ'}>
          BEAM AZ
        </div>
      ),
      accessor: 'beamAz',
      sortable: false,
      Cell: (row) => <div className="HopDistanceCellWrapper">{row.original.beamAz}</div>
    },
    {
      Header: (
        <div className="HopDetailsTableColumnHeader" data-tip={'BEAM EL'}>
          BEAM EL
        </div>
      ),
      accessor: 'beamEl',
      sortable: false,
      Cell: (row) => <div className="HopDistanceCellWrapper">{row.original.beamEl}</div>
    },
    {
      Header: (
        <div className="HopDetailsTableColumnHeader" data-tip={'ANT AZ (Optional)'}>
          ANT AZ *
        </div>
      ),
      accessor: 'antAz',
      sortable: false,
      Cell: (row) => {
        return (
          <div className="HopDetailsTableAzContainer">
            <input
              className="antAzInput"
              type="number"
              autoFocus={isInputFocus(row.original.pivot + row.index.toString())}
              placeholder={'Enter Value'}
              value={getAzInputValue(row.original.pivot, row.index)}
              onChange={(evt) => {
                setEditingRowString(row.original.pivot + row.index.toString());

                const targetValue = evt.target.value;
                azChangeDictionary.current[row.original.deviceIdUserLabel.deviceId] = targetValue;
                const rowIndex = row.index;
                if (row.original.pivot === 'pivot1') {
                  setPivot1AzValues([
                    ...pivot1AzValues.slice(0, rowIndex),
                    targetValue,
                    ...pivot1AzValues.slice(rowIndex + 1)
                  ]);
                } else {
                  setPivot2AzValues([
                    ...pivot2AzValues.slice(0, rowIndex),
                    targetValue,
                    ...pivot2AzValues.slice(rowIndex + 1)
                  ]);
                }
              }}
            />
          </div>
        );
      }
    }
  ];

  const hopDistanceColumns = [
    {
      Header: (
        <div className="HopDistanceTableColumnHeader" data-tip={'PRODUCT TYPE'}>
          DISTANCE-METERS
        </div>
      ),
      // minWidth: 300,
      width: 260,
      accessor: 'pivot',
      Cell: (row) => {
        return (
          <div className="distanceHopLabel">
            <IconAndText img={IconLoader.icon_service} text={'Pivot1 SU1 HBF'} />
            <img src={IconLoader.right_arrow} alt="" />
            <IconAndText img={IconLoader.icon_donor} text={'Pivot2 DU2'} />
          </div>
        );
      },
      sortable: false
    },
    {
      Header: <div className="HopDistanceTableColumnHeader_Disabled">FIELD</div>,
      width: 85,
      accessor: 'field',
      sortable: false
    },
    {
      Header: <div className="HopDistanceTableColumnHeader_Disabled">CALC QB</div>,
      width: 88,
      accessor: 'calcQb',
      sortable: false
    },
    {
      Header: <div className="HopDistanceTableColumnHeader">CALC TWIN</div>,
      width: 88,
      accessor: 'calcTwin',
      sortable: false,
      Cell: (row) => (
        <div className="HopDistanceCellWrapper" data-tip={row.original.calcTwin}>
          {getTruncatedCellDisplay(row.original.calcTwin)}
        </div>
      )
    },
    {
      Header: <div className="HopDistanceTableColumnHeader">AVERAGE</div>,
      width: 88,
      accessor: 'avg',
      sortable: false,
      Cell: (row) => <div className="HopDistanceCellWrapper">{row.original.avg}</div>
    }
  ];

  const hopAzimuthColumns = [
    {
      Header: <div className="HopDistanceTableColumnHeader">AZIMUTH (DEGREES)</div>,
      // minWidth: 300,
      width: 272,
      accessor: 'pivot',
      Cell: (row) => {
        if (row.index === 0) {
          return (
            <div className="distanceHopLabel">
              <IconAndText img={IconLoader.icon_service} text={'Pivot1 SU1 HBF'} />
              <img src={IconLoader.right_arrow} alt="" />
              <IconAndText img={IconLoader.icon_donor} text={'Pivot2 DU2'} />
            </div>
          );
        }
        return (
          <div className="distanceHopLabel">
            <IconAndText img={IconLoader.icon_donor} text={'Pivot2 DU2'} />
            <img src={IconLoader.right_arrow} alt="" />
            <IconAndText img={IconLoader.icon_service} text={'Pivot1 SU1 HBF'} />
          </div>
        );
      },
      sortable: false
    },
    {
      Header: <div className="HopDistanceTableColumnHeader">FIELD</div>,
      width: 85,
      accessor: 'field',
      sortable: false,
      Cell: (row) => {
        let value;
        if (row.index === 0) {
          value = pivot1AzValues[1];
        } else {
          value = pivot2AzValues[0];
        }

        return (
          <div className="HopDistanceCellWrapper" data-tip={value}>
            {getTruncatedCellDisplay(value)}
          </div>
        );
      }
    },
    {
      Header: <div className="HopDistanceTableColumnHeader_Disabled">CALC QB</div>,
      width: 88,
      accessor: 'calcQb',
      sortable: false,
      Cell: (row) => <div className="HopDistanceCellWrapper">{row.original.calcQb}</div>
    },
    {
      Header: <div className="HopDistanceTableColumnHeader">CALC TWIN</div>,
      width: 88,
      accessor: 'calcTwin',
      sortable: false,
      Cell: (row) => {
        const fullNumber = row.original.calcTwin;
        if (!fullNumber) return null;
        return (
          <div className="HopDistanceCellWrapper" data-tip={fullNumber}>
            {getTruncatedCellDisplay(fullNumber)}
          </div>
        );
      }
    },
    {
      Header: <div className="HopDistanceTableColumnHeader">STD DEV</div>,
      width: 88,
      accessor: 'avg',
      sortable: false,
      Cell: (row) => {
        const fullNumber = AzStdDevs[row.index];
        if (!fullNumber) return null;
        return (
          <div className="HopDistanceCellWrapper" data-tip={fullNumber}>
            {getTruncatedCellDisplay(fullNumber)}
          </div>
        );
      }
    }
  ];

  const getAzInputValue = (pivot, index) => {
    if (pivot === 'pivot1') {
      return pivot1AzValues[index];
    }

    return pivot2AzValues[index];
  };

  const isInputFocus = (pivotString) => {
    return pivotString === editingRowString;
  };

  const getTwoHopDistanceTwin = () => {
    const device1 = props.twinDict[props.mapperDict.hop1service1];
    const device2 = props.twinDict[props.mapperDict.hop2donor1];
    const lat1 = device1?.properties?.reported?.location?.lat;
    const long1 = device1?.properties?.reported?.location?.long;
    const lat2 = device2?.properties?.reported?.location?.lat;
    const long2 = device2?.properties?.reported?.location?.long;

    if ([lat1, long1, lat2, long2].some((value) => value === undefined)) {
      return 'Unknown';
    }

    return getDistance({ latitude: lat1, longitude: long1 }, { latitude: lat2, longitude: long2 });
  };

  const getTwoHopDistanceAverage = () => {
    // temp as twin is the only value for now
    const device1 = props.twinDict[props.mapperDict.hop1service1];
    const device2 = props.twinDict[props.mapperDict.hop2donor1];
    return getTwoHopDistanceTwin(device1, device2);
  };

  const getTwoHopBearing = (device1, device2) => {
    const lat1 = device1?.properties?.reported?.location?.lat;
    const long1 = device1?.properties?.reported?.location?.long;
    const lat2 = device2?.properties?.reported?.location?.lat;
    const long2 = device2?.properties?.reported?.location?.long;

    if ([lat1, long1, lat2, long2].some((value) => value === undefined)) {
      return 'Unknown';
    }

    return getRhumbLineBearing({ latitude: lat1, longitude: long1 }, { latitude: lat2, longitude: long2 });
  };

  const getStdDev = (index) => {
    if (index === 0) {
      const bearing = getTwoHopBearing(
        props.twinDict[props.mapperDict.hop1service1],
        props.twinDict[props.mapperDict.hop2donor1]
      );

      const azInput = pivot1AzValues[1];

      if (bearing === 'Unknown' || isNaN(azInput)) {
        return null;
      }

      return parseFloat(azInput) - bearing;
    }

    const bearing = getTwoHopBearing(
      props.twinDict[props.mapperDict.hop2donor1],
      props.twinDict[props.mapperDict.hop1service1]
    );

    const azInput = pivot2AzValues[0];

    if (bearing === 'Unknown' || isNaN(azInput)) {
      return null;
    }

    return parseFloat(azInput) - bearing;
  };

  const getTruncatedCellDisplay = (label) => {
    if (label === undefined) return '';
    const labelString = label.toString();
    if (labelString.length < 10) return labelString;

    return labelString.substring(0, 8) + '...';
  };

  const massPatchAz = () => {
    let ids = Object.keys(azChangeDictionary.current);
    ids.forEach((id) => {
      dispatch(patchDeviceParameters(id, { tags: { antenna_az: azChangeDictionary.current[id] } }, () => {}, true));
    });
  };

  const createData = () => {
    let hop1arr = [props.mapperDict.hop1donor1, props.mapperDict.hop1service1, props.mapperDict.hop1service2];
    let hop2arr = [props.mapperDict.hop2donor1, props.mapperDict.hop2service1, props.mapperDict.hop2service2];

    hop1arr.forEach((d) => {
      if (d != null && d !== '')
        hop1Data.current.push({
          pivot: 'pivot1',
          deviceIdUserLabel: { deviceId: props?.twinDict[d]?.deviceId, userLabel: props?.twinDict[d]?.tags?.userLabel },
          productType: props?.twinDict[d]?.properties?.reported?.productType,
          pivotStatus: 'Comm',
          lat: props?.twinDict[d]?.properties?.reported?.location?.lat,
          long: props?.twinDict[d]?.properties?.reported?.location?.long,
          beamAz: props?.twinDict[d]?.properties?.reported?.antenna_direction?.beamAzimuth,
          beamEl: props?.twinDict[d]?.properties?.reported?.antenna_direction?.beamElevation,
          antAz: null
        });
    });

    hop2arr.forEach((d) => {
      if (d != null && d !== '')
        hop2Data.current.push({
          pivot: 'pivot2',
          deviceIdUserLabel: { deviceId: props?.twinDict[d]?.deviceId, userLabel: props?.twinDict[d]?.tags?.userLabel },
          productType: props?.twinDict[d]?.properties?.reported?.productType,
          pivotStatus: 'Comm',
          lat: props?.twinDict[d]?.properties?.reported?.location?.lat,
          long: props?.twinDict[d]?.properties?.reported?.location?.long,
          beamAz: props?.twinDict[d]?.properties?.reported?.antenna_direction?.beamAzimuth,
          beamEl: props?.twinDict[d]?.properties?.reported?.antenna_direction?.beamElevation,
          antAz: null
        });
    });

    hopDistanceData.current = [
      {
        field: null,
        calcQb: null,
        calcTwin: getTwoHopDistanceTwin(),
        avg: getTwoHopDistanceAverage()
      }
    ];

    hopAzData.current = [
      {
        // field: azChangeDictionary.current[props.mapperDict.hop1service1],
        calcQb: null,
        calcTwin: getTwoHopBearing(
          props.twinDict[props.mapperDict.hop1service1],
          props.twinDict[props.mapperDict.hop2donor1]
        )
        // StdDev: getStdDev(0)
      },
      {
        // field: azChangeDictionary.current[props.mapperDict.hop2donor1],
        calcQb: null,
        calcTwin: getTwoHopBearing(
          props.twinDict[props.mapperDict.hop2donor1],
          props.twinDict[props.mapperDict.hop1service1]
        )
        // StdDev: getStdDev(1)
      }
    ];

    setRenderKey(v4());
    setPivot1AzValues(Array.apply(null, Array(hop1arr.length)).map(() => {}));
    setPivot2AzValues(Array.apply(null, Array(hop2arr.length)).map(() => {}));
  };

  const shouldDisplayDistanceWarning = () => {
    const averageDistance = getTwoHopDistanceAverage();
    return !isNaN(averageDistance) && parseFloat(averageDistance) > 80;
  };

  const shouldDisplayAzWarning = () => AzStdDevs.some((az) => !isNaN(az) && Math.abs(az) > 15);

  const getWarningBox = (distanceWarning, azWarning) => {
    if (!distanceWarning && !azWarning) return null;

    let warningMessages = [];

    if (distanceWarning) warningMessages.push(DISTANCE_WARNING_MESSAGE);
    if (azWarning) warningMessages.push(AZ_WARNING_MESSAGE);

    return (
      <div className="HopWizardPivotDetailsContent_WarningContainer">
        <WarningBox warningMessages={warningMessages} className={'HopWizardTable_HeaderWarning'} />
      </div>
    );
  };

  return (
    <Modal
      animateX={false}
      global
      close={props.onClose}
      noPadding
      // displayFlex={true}
      overflow={true}
      height={'fit-content'}
      width={1400}
    >
      <div className="HopWizardPivotDetails">
        <div className="HopWizardPivotDetailsHeader">Two Hop Details</div>
        <div className="HopWizardPivotDetailsContent">
          <div className="HopWizardPivotDetailsContentTableBox">
            <div className="HopWizardPivotDetailsContentTableBoxHeader">
              <div className="HopWizardPivotDetailsContentTableBoxHeaderTitle">Two Hop Summary</div>
            </div>

            <ReactTable
              className={'Hop2Table'}
              minRows={2}
              showPagination={false}
              resizable={false}
              getTdProps={(state, rowInfo, column, instance) => {
                return {
                  onClick: (e, handleOriginal) => {
                    if (handleOriginal) {
                      handleOriginal(); /* Suggested by documentation, makes sure that default click functions still get called if necessary*/
                    }
                  }
                };
              }}
              data={hop1Data.current}
              columns={hopColumns(hop1Data.current)}
              getProps={() => {
                return { id: 'hop1-table' };
              }}
            />
            <ReactTable
              className={'Hop2Table'}
              minRows={2}
              showPagination={false}
              resizable={false}
              getTdProps={(state, rowInfo, column, instance) => {
                return {
                  onClick: (e, handleOriginal) => {
                    if (handleOriginal) {
                      handleOriginal(); /* Suggested by documentation, makes sure that default click functions still get called if necessary*/
                    }
                  }
                };
              }}
              data={hop2Data.current}
              columns={hopColumns(hop2Data.current)}
              getProps={() => {
                return { id: 'hop2-table' };
              }}
            />
            <div className="HopWizardPivotDetailsContentTableBoxFooter">
              <div
                className="HopWizardPivotDetailsContentTableBoxFooterCancel"
                onClick={() => {
                  setPivot1AzValues(Array.apply(null, Array(3)).map(() => {}));
                  setPivot2AzValues(Array.apply(null, Array(3)).map(() => {}));
                  setEditingRowString(undefined);
                }}
              >
                Cancel
              </div>
              <div
                className="HopWizardPivotDetailsContentTableBoxFooterSave"
                onClick={() => {
                  setEditingRowString(undefined);
                  massPatchAz();
                }}
              >
                Save
              </div>
            </div>
          </div>

          <div className="HopWizardPivotDetailsDistanceTableBox">
            <div className="HopWizardPivotDetailsContent_Tables">
              <div className="HopWizardPivotDetailsContent_TableContainer">
                <div className="HopWizardPivotDetailsContent_TableHeader">
                  <span className="HopWizardTableHeader">Distance (Meters)</span>
                  <span className="HopWizardTableSubHeader">Data Source - Calculated</span>
                </div>
                <ReactTable
                  className={'HopDistanceTable'}
                  defaultPageSize={1}
                  showPagination={false}
                  resizable={false}
                  getTdProps={(state, rowInfo, column, instance) => {
                    return {
                      onClick: (e, handleOriginal) => {
                        if (handleOriginal) {
                          handleOriginal(); /* Suggested by documentation, makes sure that default click functions still get called if necessary*/
                        }
                      }
                    };
                  }}
                  data={hopDistanceData.current}
                  columns={hopDistanceColumns}
                  getProps={() => {
                    return { id: 'hop2-table' };
                  }}
                />
              </div>
              <div className="HopWizardPivotDetailsContent_TableContainer">
                <div className="HopWizardPivotDetailsContent_TableHeader">
                  <span className="HopWizardTableHeader">Azimuth (Degrees from True North)</span>
                  <span className="HopWizardTableSubHeader">Data Source - Calculated</span>
                </div>
                <ReactTable
                  // className={'HopDistanceTable'}
                  defaultPageSize={2}
                  showPagination={false}
                  resizable={false}
                  getTdProps={(state, rowInfo, column, instance) => {
                    return {
                      onClick: (e, handleOriginal) => {
                        if (handleOriginal) {
                          handleOriginal(); /* Suggested by documentation, makes sure that default click functions still get called if necessary*/
                        }
                      }
                    };
                  }}
                  data={hopAzData.current}
                  columns={hopAzimuthColumns}
                  getProps={() => {
                    return { id: 'hop2-table' };
                  }}
                />
              </div>
            </div>
            {getWarningBox(shouldDisplayDistanceWarning(), shouldDisplayAzWarning())}
          </div>
        </div>
        <div className="HopWizardPivotDetailsFooter">
          <div className="HopWizardPivotDetailsFooterCancelButton" onClick={props.onClose}>
            Cancel
          </div>
          <button
            className="HopWizardPivotDetailsFooterNextButton"
            onClick={() => props.onNext(TWO_HOP_ALIGNMENT_SCREEN_INDEXES.PIVOT_DETAILS)}
          >
            Next
          </button>
        </div>
      </div>
    </Modal>
  );
};
